import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';

import { listMyInvoices, retrieveMyPaymentStanding } from '@src/api/invoices';
import paths from '@src/paths';

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '\u2014';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '\u2014');

const STATUS_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Unpaid', value: 'unpaid' },
  { label: 'Past Due', value: 'past_due' },
  { label: 'Paid', value: 'paid' },
];

const STATUS_CHIP_PROPS = {
  sent: { label: 'Unpaid', color: 'info', variant: 'outlined' },
  outstanding: { label: 'Past Due', color: 'warning', variant: 'filled' },
  paid: { label: 'Paid', color: 'success', variant: 'filled' },
  void: { label: 'Voided', color: 'default', variant: 'outlined' },
};

const URGENCY_CONFIG = {
  critical: { label: 'Account at risk of pause', color: 'error', variant: 'filled' },
  final: { label: 'Final notice \u2014 pause imminent', color: 'error', variant: 'outlined' },
  warning: { label: 'Past due', color: 'warning', variant: 'filled' },
  overdue: { label: 'Overdue', color: 'warning', variant: 'outlined' },
};

function PaymentStandingCard({ standing }) {
  if (!standing || !standing.applies) return null;
  const count = standing.strike_count || 0;
  const limit = standing.strike_limit || 3;
  const months = Math.round((standing.window_days || 365) / 30);
  const atLimit = count >= limit;
  const good = count === 0;
  const color = good ? 'success' : atLimit ? 'error' : 'warning';

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 2, borderLeft: '4px solid', borderLeftColor: `${color}.main` }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Payment standing
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700, color: `${color}.main` }}>
                {good ? 'Good standing' : `${count} of ${limit} late payments`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {Array.from({ length: limit }, (_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: i < count ? `${color}.main` : 'action.disabledBackground',
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
            {good
              ? `Invoices paid more than 6 days late count toward your payment standing for ${months} months. `
              : `An invoice counts as a late payment when it goes 6+ days past due (the point lead delivery is paused). Late payments stay on your standing for ${months} months. `}
            After {limit} late payments, we&apos;ll reach out about moving your account to client-funded ad spend billing.
          </Typography>
        </Stack>
        {count > 0 && Array.isArray(standing.strikes) && standing.strikes.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {standing.strikes
              .map((s) => `Invoice ${s.invoice_number} (${dayjs(s.struck_at).format('MMM D, YYYY')})`)
              .join(' · ')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function StatusChip({ status, urgency, daysPastDue }) {
  if (urgency && URGENCY_CONFIG[urgency]) {
    const cfg = URGENCY_CONFIG[urgency];
    const daysLabel = daysPastDue != null ? ` (${daysPastDue}d)` : '';
    return <Chip size="small" label={`${cfg.label}${daysLabel}`} color={cfg.color} variant={cfg.variant} />;
  }
  const props = STATUS_CHIP_PROPS[status] || { label: status || '\u2014', color: 'default', variant: 'outlined' };
  return <Chip size="small" {...props} />;
}

export default function ClientInvoiceListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allInvoices, setAllInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [standing, setStanding] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    listMyInvoices({ ordering: '-issue_date', page_size: 200 })
      .then((res) => {
        const results = res?.data?.results || res?.data || [];
        setAllInvoices(Array.isArray(results) ? results : []);
      })
      .catch((err) => {
        console.error('Unable to load invoices', err);
        const detail = err?.response?.data?.detail;
        setError(detail || 'Unable to load invoices.');
      })
      .finally(() => setLoading(false));
    retrieveMyPaymentStanding()
      .then((res) => setStanding(res?.data || null))
      .catch(() => setStanding(null));
  }, []);

  const invoices = useMemo(() => {
    if (statusFilter === 'all') return allInvoices;
    // "Unpaid" tab = sent + outstanding (all invoices with a balance due)
    if (statusFilter === 'unpaid') {
      return allInvoices.filter((inv) => {
        const s = String(inv.status).toLowerCase();
        return s === 'outstanding' || s === 'sent';
      });
    }
    // "Past Due" tab = only outstanding (due date has passed)
    if (statusFilter === 'past_due') {
      return allInvoices.filter((inv) => String(inv.status).toLowerCase() === 'outstanding');
    }
    return allInvoices.filter((inv) => String(inv.status).toLowerCase() === statusFilter);
  }, [allInvoices, statusFilter]);

  const handleTabChange = (event, newValue) => {
    setStatusFilter(newValue);
  };

  const handleRowClick = (invoice) => {
    navigate(paths.myInvoices.details(invoice.number));
  };

  const summaryStats = useMemo(() => {
    let unpaidCount = 0;
    let unpaidTotal = 0;
    allInvoices.forEach((inv) => {
      const s = String(inv.status || '').toLowerCase();
      if (s === 'outstanding' || s === 'sent') {
        unpaidCount += 1;
        unpaidTotal += parseFloat(inv.balance_due || inv.total_amount || 0);
      }
    });
    return { unpaidCount, unpaidTotal };
  }, [allInvoices]);

  const criticalInvoices = useMemo(
    () => allInvoices.filter((inv) => inv.urgency === 'critical' || inv.urgency === 'final'),
    [allInvoices],
  );
  const warningInvoices = useMemo(
    () => allInvoices.filter((inv) => inv.urgency === 'warning' || inv.urgency === 'overdue'),
    [allInvoices],
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>Invoices &middot; Lead Gurus</title>
      </Helmet>

      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and pay your invoices
          </Typography>
        </Box>

        {criticalInvoices.length > 0 && (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Your account is at risk of being paused
            </Typography>
            <Typography variant="body2">
              {criticalInvoices.length === 1
                ? `Invoice ${criticalInvoices[0].number} is ${criticalInvoices[0].days_past_due}+ days past due. `
                : `${criticalInvoices.length} invoices are 5+ days past due. `}
              Lead delivery will be paused until payment is received. Please pay immediately to avoid disruption.
            </Typography>
          </Alert>
        )}

        {warningInvoices.length > 0 && criticalInvoices.length === 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              You have past-due invoices
            </Typography>
            <Typography variant="body2">
              {warningInvoices.length === 1
                ? `Invoice ${warningInvoices[0].number} is ${warningInvoices[0].days_past_due} day${warningInvoices[0].days_past_due !== 1 ? 's' : ''} past due. `
                : `${warningInvoices.length} invoices are past due. `}
              Accounts are paused at 6 days overdue. Please submit payment to keep your lead flow running.
            </Typography>
          </Alert>
        )}

        <PaymentStandingCard standing={standing} />

        {!loading && !error && summaryStats.unpaidCount > 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Unpaid
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {formatCurrency(summaryStats.unpaidTotal)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summaryStats.unpaidCount} invoice{summaryStats.unpaidCount !== 1 ? 's' : ''} due
              </Typography>
            </CardContent>
          </Card>
        )}

        <Tabs value={statusFilter} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {STATUS_TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : invoices.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                {statusFilter === 'all'
                  ? 'No invoices found.'
                  : `No ${STATUS_TABS.find((t) => t.value === statusFilter)?.label?.toLowerCase() || ''} invoices.`}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Balance Due</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => {
                    const balanceDue = parseFloat(invoice.balance_due || 0);
                    const s = String(invoice.status).toLowerCase();
                    const isOverdue =
                      (s === 'outstanding' || s === 'sent') &&
                      invoice.due_date &&
                      dayjs(invoice.due_date).isBefore(dayjs(), 'day');

                    return (
                      <TableRow
                        key={invoice.id || invoice.number}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(invoice)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {invoice.number}
                          </Typography>
                          {invoice.client_name ? (
                            <Typography variant="caption" color="text.secondary">
                              {invoice.client_name}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {invoice.period_label || `${formatDate(invoice.period_start)} \u2013 ${formatDate(invoice.period_end)}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <StatusChip status={invoice.status} urgency={invoice.urgency} daysPastDue={invoice.days_past_due} />
                            {invoice.late_strike ? (
                              <Chip size="small" label="Late payment" color="error" variant="outlined" />
                            ) : invoice.paid_days_late > 0 ? (
                              <Chip
                                size="small"
                                label={`Paid ${invoice.paid_days_late}d late`}
                                color="warning"
                                variant="outlined"
                              />
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(invoice.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: balanceDue > 0 ? 600 : 400,
                              color: isOverdue ? 'error.main' : balanceDue > 0 ? 'warning.main' : 'text.secondary',
                            }}
                          >
                            {formatCurrency(invoice.balance_due)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={isOverdue ? 'error.main' : 'text.primary'}
                          >
                            {formatDate(invoice.due_date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {(s === 'sent' || s === 'outstanding') ? (
                            <Button
                              size="small"
                              variant="contained"
                              color={isOverdue ? 'error' : 'primary'}
                              onClick={(e) => { e.stopPropagation(); handleRowClick(invoice); }}
                              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              Pay Now
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="text"
                              onClick={(e) => { e.stopPropagation(); handleRowClick(invoice); }}
                              sx={{ textTransform: 'none' }}
                            >
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
